import { useEffect, useRef, useState } from "react";
import { apiGet, apiPost } from "../../lib/api";
import { IconChat } from "../ui/icons";

// Same var Layout.tsx's footer link uses — public by design, not a secret.
const TELEGRAM_USERNAME = import.meta.env.VITE_TELEGRAM_USERNAME as string | undefined;

interface ChatMessage {
  from: "visitor" | "operator";
  text: string;
  timestamp: string;
  reason?: string;
}

const CONVERSATION_ID_KEY = "chatConversationId";

function getConversationId(): string {
  let id = localStorage.getItem(CONVERSATION_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(CONVERSATION_ID_KEY, id);
  }
  return id;
}

const POLL_INTERVAL_MS = 3000;

// Shown once, before a visitor's first message in a conversation — helps
// route/triage what's actually a recovery question, a trace question, or
// something else, without another field to fill in later.
const REASONS = [
  "Missing word(s) in my seed phrase",
  "Incorrect word in my seed phrase",
  "Partial private key",
  "Unknown wallet / derivation path",
  "Tracing a transaction",
  "Billing / payment issue",
  "Something else",
];

/**
 * Floating site-wide contact widget, bridged to the operator's Telegram
 * (see services/chain-data-api/src/contactRoutes.ts). Deliberately
 * separate from Recover/Trace — this is a general "talk to a person"
 * channel, not part of either tool's flow, and never should be treated
 * as one: the warning note below is load-bearing, not decorative.
 */
export default function ChatWidget() {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [reason, setReason] = useState("");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nextIndexRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Synchronous re-entrancy guard for send() — `sending` state alone has a
  // window where two near-simultaneous triggers (double-click, Enter right
  // after a click) both read the stale pre-update value before React
  // re-renders the disabled button, letting both through.
  const sendingLockRef = useRef(false);

  useEffect(() => {
    apiGet<{ enabled: boolean }>("/api/contact/enabled")
      .then((r) => setEnabled(r.enabled))
      .catch(() => setEnabled(false));
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function poll() {
      try {
        const conversationId = getConversationId();
        const { messages: newMessages, nextIndex } = await apiGet<{ messages: ChatMessage[]; nextIndex: number }>(
          `/api/contact/messages/${conversationId}?since=${nextIndexRef.current}`
        );
        if (cancelled) return;
        if (newMessages.length > 0) {
          nextIndexRef.current = nextIndex;
          setMessages((prev) => [...prev, ...newMessages]);
        }
      } catch {
        // Silent — the next poll tick retries. Not worth surfacing a
        // transient network blip as a visible error for a background poll.
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    }

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Only true before the conversation's first message — the reason picker
  // is a one-time step, not something asked on every message.
  const needsReason = !loadingHistory && messages.length === 0;

  async function send() {
    const text = input.trim();
    if (!text || sending || sendingLockRef.current) return;
    if (needsReason && !reason) return;
    sendingLockRef.current = true;
    setSending(true);
    setError(null);
    try {
      const conversationId = getConversationId();
      const body = needsReason ? { conversationId, text, reason } : { conversationId, text };
      await apiPost("/api/contact/message", body);
      setMessages((prev) => [...prev, { from: "visitor", text, timestamp: new Date().toISOString(), ...(needsReason ? { reason } : {}) }]);
      // The server just persisted exactly this one message as the next
      // entry in the conversation. Advance the poll cursor past it now,
      // not just when poll() itself resolves — otherwise the poll tick
      // that fires before its own next update sees the old cursor, re-fetches
      // this same message from the server, and appends it a second time
      // right next to the optimistic copy above.
      nextIndexRef.current += 1;
      setInput("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      sendingLockRef.current = false;
      setSending(false);
    }
  }

  if (!enabled) return null;

  return (
    <div className="fixed top-20 right-5 z-40 flex flex-col items-end">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-full bg-ledger-500 text-ink-950 shadow-lg pl-3 pr-4 py-3 hover:brightness-110 transition"
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {open ? (
          <span className="w-6 h-6 flex items-center justify-center text-lg leading-none">✕</span>
        ) : (
          <IconChat className="w-6 h-6" />
        )}
        <span className="text-sm font-medium whitespace-nowrap">{open ? "Close chat" : "Chat with us"}</span>
      </button>

      {open && (
        <div className="mt-3 w-80 max-w-[calc(100vw-2.5rem)] rounded-lg border border-ink-700 bg-ink-950 shadow-xl flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-ink-700 flex items-center justify-between">
            <span className="font-display text-sm">Chat with us</span>
            <button onClick={() => setOpen(false)} className="text-muted hover:text-paper" aria-label="Close chat">
              ✕
            </button>
          </div>

          <div className="flex-1 max-h-80 overflow-y-auto px-4 py-3 space-y-2">
            {!loadingHistory && messages.length === 0 && (
              <>
                <p className="text-xs text-muted">Tell us what's going on — we usually reply within a day.</p>
                {TELEGRAM_USERNAME && (
                  <p className="text-xs text-muted">
                    Prefer Telegram?{" "}
                    <a
                      href={`https://t.me/${TELEGRAM_USERNAME}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-ledger-400 hover:underline"
                    >
                      Message @{TELEGRAM_USERNAME} directly
                    </a>
                    .
                  </p>
                )}
              </>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.from === "visitor" ? "items-end" : "items-start"}`}>
                {m.reason && <span className="text-[10px] text-muted mb-0.5">Re: {m.reason}</span>}
                <p
                  className={`max-w-[85%] rounded-md px-3 py-1.5 text-sm break-words ${
                    m.from === "visitor" ? "bg-ledger-500/20 text-paper" : "bg-ink-800 text-paper"
                  }`}
                >
                  {m.text}
                </p>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="px-4 pt-2 pb-3 border-t border-ink-700">
            <p className="text-[11px] text-red-400 mb-2">
              Never share your seed phrase, private key, or password here.
            </p>
            {error && <p className="text-xs text-red-400 mb-2">{error}</p>}

            {needsReason && (
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full mb-2 bg-ink-900 border border-ink-700 rounded-md px-2 py-1.5 text-sm text-paper"
              >
                <option value="">What can we help with?</option>
                {REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            )}

            <div className="flex gap-2">
              {needsReason ? (
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Describe your situation — any details help (e.g. how many words you have, wallet type)…"
                  rows={3}
                  className="flex-1 min-w-0 bg-ink-900 border border-ink-700 rounded-md px-3 py-1.5 text-sm resize-none"
                />
              ) : (
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Type a message…"
                  className="flex-1 min-w-0 bg-ink-900 border border-ink-700 rounded-md px-3 py-1.5 text-sm"
                />
              )}
              <button
                onClick={send}
                disabled={sending || !input.trim() || (needsReason && !reason)}
                className="shrink-0 self-start px-3 py-1.5 rounded-md bg-ledger-500 text-ink-950 text-sm font-medium disabled:opacity-50"
              >
                {needsReason ? "Start" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
